import Link from 'next/link';
import { Typography } from '@workspace/ui/common';
import ExpandButton from './ExpandButton';
import type { IMegaMenuNavigationItemColumn } from '../../Header/interfaces';
import { getActivePathFromLink, cn } from '@/app/lib';
import { theme } from '@workspace/ui/theme';

interface MenuItemProps {
  item: IMegaMenuNavigationItemColumn;
  isExpanded?: boolean;
  hideArrow?: boolean;
  onItemClick?: (item: IMegaMenuNavigationItemColumn) => void;
  onToggleExpanded?: (id: string) => void;
  /** Show the chevron/expand arrow; parent can disable on desktop */
  showChevron?: boolean;
}

export default function MenuItem({
  item,
  isExpanded,
  hideArrow,
  onItemClick,
  onToggleExpanded,
  showChevron = true,
}: MenuItemProps) {
  const href = getActivePathFromLink(item.link) || '#';

  return (
    <div className={cn('flex flex-col gap-xs', theme === 'redRec' ? 'w-1/5 max-w-54' : 'w-11/12 max-w-40')}>
      <div className="flex items-center justify-between group font-body">
        <Link
          href={href}
          rel="noopener noreferrer"
          onClick={(e) => {
            const shouldOpenInNewTab = item.link?.type === 'external' || !!item.link?.isOpenInNewTab;
            const hasValidHref = href && href !== '#';

            if (shouldOpenInNewTab && hasValidHref) {
              e.preventDefault();
              window.open(href, '_blank', 'noopener,noreferrer');
              return;
            }

            if (!hasValidHref) {
              e.preventDefault();
              return;
            }

            onItemClick?.(item);
          }}
          className="inline-block"
        >
          <Typography
            variant="h3"
            className={cn(
              'font-semibold text-secondary-400 transition-colors text-base! font-body leading-5!',
              item.link?.internalReference?.url || item.link?.externalUrl
                ? 'cursor-pointer'
                : 'cursor-default! no-underline!',
            )}
          >
            {item.label}
          </Typography>
        </Link>
        {(item.childrenColumn?.length || 0) > 0 && !hideArrow && showChevron && (
          <ExpandButton isExpanded={isExpanded} onClick={() => onToggleExpanded?.(item._key)} />
        )}
      </div>

      {(item.childrenColumn?.length || 0) > 0 && isExpanded && (
        <ul className="space-y-2xs">
          {(item.childrenColumn || []).map((subitem) => {
            const href = getActivePathFromLink(subitem.link) || '#';

            return (
              <li key={subitem._key}>
                <Link
                  href={href}
                  onClick={(e) => {
                    const shouldOpenInNewTab = subitem.link?.type === 'external' || !!subitem.link?.isOpenInNewTab;
                    const hasValidHref = href && href !== '#';

                    if (shouldOpenInNewTab && hasValidHref) {
                      e.preventDefault();
                      window.open(href, '_blank', 'noopener,noreferrer');
                      return;
                    }

                    if (!hasValidHref) {
                      e.preventDefault();
                      return;
                    }

                    onItemClick?.(subitem);
                  }}
                  rel="noopener noreferrer"
                  className={cn(
                    'relative inline-block',
                    'text-sm text-secondary-400',
                    'font-body',
                    "after:content-[''] after:absolute after:left-0 after:-bottom-0.5",
                    'after:h-0.25 after:w-full after:bg-black',
                    'after:origin-left after:scale-x-0',
                    (subitem.link?.internalReference?.url || subitem.link?.externalUrl) &&
                      'hover:after:scale-x-100 focus-visible:after:scale-x-100',
                    subitem.link?.internalReference?.url || subitem.link?.externalUrl
                      ? 'cursor-pointer'
                      : 'cursor-default! hover:no-underline! hover:decoration-none!',
                  )}
                >
                  {subitem.label}
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
